const sortFoldersByName = (folders: { value: string; label: string }[]) => {
  return [...folders].sort((a, b) => {
    const aLabel = a.label.trim();
    const bLabel = b.label.trim();

    // Helper to check if string is numeric
    const isNumeric = (str: string): boolean => /^\d+$/.test(str);

    // Helper to parse value into components
    const parseValue = (
      val: string
    ): {
      type: "number" | "number-prefix" | "string-suffix" | "string";
      numValue?: number;
      strValue?: string;
      suffixNum?: number;
    } => {
      // Pure number
      if (isNumeric(val)) {
        return { type: "number", numValue: parseInt(val, 10) };
      }

      const parts = val.split(" ");

      // Check for "number string..." pattern (including "number string number")
      if (parts.length >= 2 && isNumeric(parts[0])) {
        const numPrefix = parseInt(parts[0], 10);
        const restParts = parts.slice(1);

        // Check if last part is also a number (for "number string number" pattern)
        if (
          restParts.length >= 2 &&
          isNumeric(restParts[restParts.length - 1])
        ) {
          // "number string number" pattern
          const suffixNum = parseInt(restParts[restParts.length - 1], 10);
          const middleParts = restParts.slice(0, -1);

          return {
            type: "number-prefix",
            numValue: numPrefix,
            strValue: middleParts.join(" "),
            suffixNum: suffixNum,
          };
        }

        // Regular "number string" pattern
        return {
          type: "number-prefix",
          numValue: numPrefix,
          strValue: restParts.join(" "),
        };
      }

      // Check for "string... number" pattern (but not "number string number" which was handled above)
      if (parts.length >= 2 && isNumeric(parts[parts.length - 1])) {
        const beforeLast = parts.slice(0, -1).join(" ");
        const lastNum = parseInt(parts[parts.length - 1], 10);

        return {
          type: "string-suffix",
          strValue: beforeLast,
          suffixNum: lastNum,
        };
      }

      // Pure string
      return { type: "string", strValue: val };
    };

    const aParsed = parseValue(aLabel);
    const bParsed = parseValue(bLabel);

    // Pure numbers: compare numerically
    if (aParsed.type === "number" && bParsed.type === "number") {
      return aParsed.numValue! - bParsed.numValue!;
    }

    // Number comes before non-number
    if (aParsed.type === "number" && bParsed.type !== "number") {
      return -1;
    }
    if (aParsed.type !== "number" && bParsed.type === "number") {
      return 1;
    }

    // "number-prefix" vs "number-prefix"
    if (aParsed.type === "number-prefix" && bParsed.type === "number-prefix") {
      // First compare string parts (the middle part)
      const strCompare = aParsed.strValue!.localeCompare(bParsed.strValue!);
      if (strCompare !== 0) {
        return strCompare;
      }
      // If strings are equal, compare numeric prefixes
      const numCompare = aParsed.numValue! - bParsed.numValue!;
      if (numCompare !== 0) {
        return numCompare;
      }
      // If both are equal and there are suffix numbers, compare those
      if (aParsed.suffixNum !== undefined && bParsed.suffixNum !== undefined) {
        return aParsed.suffixNum - bParsed.suffixNum;
      }
      return 0;
    }

    // "number-prefix" comes before pure strings and "string-suffix"
    if (
      aParsed.type === "number-prefix" &&
      (bParsed.type === "string" || bParsed.type === "string-suffix")
    ) {
      return -1;
    }
    if (
      (aParsed.type === "string" || aParsed.type === "string-suffix") &&
      bParsed.type === "number-prefix"
    ) {
      return 1;
    }

    // "string-suffix" vs "string-suffix"
    if (aParsed.type === "string-suffix" && bParsed.type === "string-suffix") {
      // Compare string parts first
      const strCompare = aParsed.strValue!.localeCompare(bParsed.strValue!);
      if (strCompare !== 0) {
        return strCompare;
      }
      // If strings are equal, compare suffix numbers
      return aParsed.suffixNum! - bParsed.suffixNum!;
    }

    // "string-suffix" vs pure string, or pure string vs pure string
    // Compare alphabetically
    const aStr = aParsed.strValue || aLabel;
    const bStr = bParsed.strValue || bLabel;

    return aStr.localeCompare(bStr);
  });
};

export default sortFoldersByName;
